import { useCallback, useEffect, useMemo, useRef } from "react";

import { Button } from "../../components/Button";
import { getItemImages } from "./getItemImages";

export function MarketItemDetailPanel({
  selectedItem,
  selectedItemImage,
  setSelectedItemImage,
  showFullImage,
  setShowFullImage,
  onCloseItem,
  onReserve,
  reservingItemId,
}) {
  const modalImageStageRef = useRef(null);
  const detailImageSwipeRef = useRef(null);
  const detailDraggedRef = useRef(false);
  const modalSwipeRef = useRef({
    pointerId: null,
    startX: 0,
    startY: 0,
    axis: null,
  });
  const detailSwipeRef = useRef({
    pointerId: null,
    startX: 0,
    startY: 0,
    axis: null,
  });

  const selectedItemImages = useMemo(() => getItemImages(selectedItem), [selectedItem]);
  const isReservingSelected = selectedItem?.id && reservingItemId === selectedItem.id;

  const showPrevImage = useCallback(() => {
    setSelectedItemImage((current) => {
      const list = getItemImages(selectedItem);
      if (list.length < 2) return current;
      const idx = list.findIndex((url) => url === current);
      if (idx < 0) return current;
      const prev = (idx - 1 + list.length) % list.length;
      return list[prev];
    });
  }, [selectedItem, setSelectedItemImage]);

  const showNextImage = useCallback(() => {
    setSelectedItemImage((current) => {
      const list = getItemImages(selectedItem);
      if (list.length < 2) return current;
      const idx = list.findIndex((url) => url === current);
      if (idx < 0) return current;
      const next = (idx + 1) % list.length;
      return list[next];
    });
  }, [selectedItem, setSelectedItemImage]);

  useEffect(() => {
    if (!showFullImage || selectedItemImages.length < 2) return undefined;
    const el = modalImageStageRef.current;
    if (!el) return undefined;

    const SWIPE_MIN = 42;
    const AXIS_LOCK = 10;
    const state = modalSwipeRef.current;

    const onPointerDown = (e) => {
      if (e.pointerType === "mouse" && e.button !== 0) return;
      state.pointerId = e.pointerId;
      state.startX = e.clientX;
      state.startY = e.clientY;
      state.axis = null;
      if (typeof el.setPointerCapture === "function") {
        try {
          el.setPointerCapture(e.pointerId);
        } catch {
          /* ignore */
        }
      }
    };

    const onPointerMove = (e) => {
      if (state.pointerId !== e.pointerId) return;
      const dx = e.clientX - state.startX;
      const dy = e.clientY - state.startY;
      if (state.axis === null) {
        if (Math.abs(dx) < AXIS_LOCK && Math.abs(dy) < AXIS_LOCK) return;
        state.axis = Math.abs(dx) > Math.abs(dy) ? "h" : "v";
      }
      if (state.axis === "h" && e.cancelable) {
        e.preventDefault();
      }
    };

    const endSwipe = (e) => {
      if (state.pointerId !== e.pointerId) return;
      const dx = e.clientX - state.startX;
      const dy = e.clientY - state.startY;
      if (typeof el.releasePointerCapture === "function") {
        try {
          el.releasePointerCapture(e.pointerId);
        } catch {
          /* ignore */
        }
      }
      state.pointerId = null;
      state.axis = null;
      if (Math.abs(dx) < SWIPE_MIN || Math.abs(dx) < Math.abs(dy)) return;
      if (dx < 0) {
        showNextImage();
      } else {
        showPrevImage();
      }
    };

    el.addEventListener("pointerdown", onPointerDown, { passive: true });
    el.addEventListener("pointermove", onPointerMove, { passive: false });
    el.addEventListener("pointerup", endSwipe, { passive: true });
    el.addEventListener("pointercancel", endSwipe, { passive: true });

    return () => {
      el.removeEventListener("pointerdown", onPointerDown);
      el.removeEventListener("pointermove", onPointerMove);
      el.removeEventListener("pointerup", endSwipe);
      el.removeEventListener("pointercancel", endSwipe);
    };
  }, [showFullImage, selectedItemImages.length, showNextImage, showPrevImage]);

  useEffect(() => {
    if (!selectedItem || selectedItemImages.length < 2) return undefined;
    const el = detailImageSwipeRef.current;
    if (!el) return undefined;

    const SWIPE_MIN = 42;
    const AXIS_LOCK = 10;
    const state = detailSwipeRef.current;

    const onPointerDown = (e) => {
      if (e.pointerType === "mouse" && e.button !== 0) return;
      detailDraggedRef.current = false;
      state.pointerId = e.pointerId;
      state.startX = e.clientX;
      state.startY = e.clientY;
      state.axis = null;
      if (typeof el.setPointerCapture === "function") {
        try {
          el.setPointerCapture(e.pointerId);
        } catch {
          /* ignore */
        }
      }
    };

    const onPointerMove = (e) => {
      if (state.pointerId !== e.pointerId) return;
      const dx = e.clientX - state.startX;
      const dy = e.clientY - state.startY;
      if (Math.abs(dx) > 4 || Math.abs(dy) > 4) {
        detailDraggedRef.current = true;
      }
      if (state.axis === null) {
        if (Math.abs(dx) < AXIS_LOCK && Math.abs(dy) < AXIS_LOCK) return;
        state.axis = Math.abs(dx) > Math.abs(dy) ? "h" : "v";
      }
      if (state.axis === "h" && e.cancelable) {
        e.preventDefault();
      }
    };

    const endSwipe = (e) => {
      if (state.pointerId !== e.pointerId) return;
      const dx = e.clientX - state.startX;
      const dy = e.clientY - state.startY;
      if (typeof el.releasePointerCapture === "function") {
        try {
          el.releasePointerCapture(e.pointerId);
        } catch {
          /* ignore */
        }
      }
      state.pointerId = null;
      state.axis = null;
      if (Math.abs(dx) >= SWIPE_MIN && Math.abs(dx) >= Math.abs(dy)) {
        if (dx < 0) {
          showNextImage();
        } else {
          showPrevImage();
        }
      }
      window.setTimeout(() => {
        detailDraggedRef.current = false;
      }, 120);
    };

    el.addEventListener("pointerdown", onPointerDown, { passive: true });
    el.addEventListener("pointermove", onPointerMove, { passive: false });
    el.addEventListener("pointerup", endSwipe, { passive: true });
    el.addEventListener("pointercancel", endSwipe, { passive: true });

    return () => {
      el.removeEventListener("pointerdown", onPointerDown);
      el.removeEventListener("pointermove", onPointerMove);
      el.removeEventListener("pointerup", endSwipe);
      el.removeEventListener("pointercancel", endSwipe);
    };
  }, [selectedItem, selectedItemImages.length, showNextImage, showPrevImage]);

  return (
    <>
      <article className="marketDetail">
        <h3>{selectedItem.title}</h3>
        {selectedItemImage ? (
          <div className="marketDetail__image">
            <div
              ref={detailImageSwipeRef}
              className="marketDetail__imageStage"
              onClickCapture={(e) => {
                if (!detailDraggedRef.current) return;
                e.preventDefault();
                e.stopPropagation();
              }}
            >
              <button
                type="button"
                className="marketDetail__imageOpen"
                onClick={() => setShowFullImage(true)}
                aria-label="Открыть фото полностью"
                title="Открыть фото полностью"
              >
                <img src={selectedItemImage} alt={selectedItem.title} draggable={false} />
              </button>
            </div>
            <button
              type="button"
              className="marketDetail__zoomBtn"
              onClick={() => setShowFullImage(true)}
              aria-label="Открыть фото полностью"
              title="Открыть фото полностью"
            >
              ⤢
            </button>
          </div>
        ) : null}
        {selectedItemImages.length > 1 ? (
          <div className="marketDetail__thumbs">
            {selectedItemImages.map((url) => (
              <button
                key={url}
                type="button"
                className={`marketDetail__thumb ${selectedItemImage === url ? "isActive" : ""}`}
                onClick={() => setSelectedItemImage(url)}
                aria-label="Показать фото товара"
              >
                <img src={url} alt={selectedItem.title} />
              </button>
            ))}
          </div>
        ) : null}
        <p>{selectedItem.description}</p>
        <p className="eventsMeta">Осталось: {Number(selectedItem.stock_quantity || 0)} шт.</p>
        <div className="marketBuyRows">
          <div className="marketBuyRow">
            <span>{Number(selectedItem.price || 0).toFixed(2)} руб.</span>
            <Button
              type="button"
              disabled={Number(selectedItem.stock_quantity || 0) <= 0 || !!reservingItemId}
              onClick={() => onReserve(selectedItem.id, "rub")}
              className={isReservingSelected ? "button--busy" : ""}
            >
              {isReservingSelected ? "Забронирование..." : "Купить"}
            </Button>
          </div>
          <div className="marketBuyRow">
            <span>{Number(selectedItem.price_bonus || 0)} баллов</span>
            <Button
              type="button"
              disabled={Number(selectedItem.stock_quantity || 0) <= 0 || !!reservingItemId}
              onClick={() => onReserve(selectedItem.id, "bonus")}
              className={isReservingSelected ? "button--busy" : ""}
            >
              {isReservingSelected ? "Забронирование..." : "Купить"}
            </Button>
          </div>
        </div>
        <Button type="button" onClick={onCloseItem}>
          Назад к ленте
        </Button>
      </article>

      {showFullImage && selectedItemImage ? (
        <div
          className="marketImageModal"
          role="dialog"
          aria-modal="true"
          aria-label="Полноразмерное фото товара"
          onClick={() => setShowFullImage(false)}
        >
          <div
            className="marketImageModal__frame"
            onClick={(event) => event.stopPropagation()}
            role="presentation"
          >
            <button
              type="button"
              className="marketImageModal__close"
              onClick={(e) => {
                e.stopPropagation();
                setShowFullImage(false);
              }}
              aria-label="Закрыть полноразмерное фото"
            >
              ×
            </button>
            {selectedItemImages.length > 1 ? (
              <button
                type="button"
                className="marketImageModal__nav marketImageModal__nav--left"
                onClick={(event) => {
                  event.stopPropagation();
                  showPrevImage();
                }}
                aria-label="Предыдущее фото"
              >
                ‹
              </button>
            ) : null}
            <div
              ref={modalImageStageRef}
              className="marketImageModal__stage"
              onClick={(event) => event.stopPropagation()}
              role="presentation"
            >
              <img
                src={selectedItemImage}
                alt={selectedItem?.title || "Фото товара"}
                draggable={false}
              />
            </div>
            {selectedItemImages.length > 1 ? (
              <>
                <button
                  type="button"
                  className="marketImageModal__nav marketImageModal__nav--right"
                  onClick={(event) => {
                    event.stopPropagation();
                    showNextImage();
                  }}
                  aria-label="Следующее фото"
                >
                  ›
                </button>
                <p className="marketImageModal__hint">Свайп или перетаскивание влево/вправо</p>
              </>
            ) : null}
          </div>
        </div>
      ) : null}
    </>
  );
}
